import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "@/shared/components/layout/dashboard-layout/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/components/ui/card";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { useEffect, useState } from "react";
import { useGetOrganizations } from "@/auth/api/queries/user.queries";
import { Skeleton } from "@/ui/components/ui/skeleton";
import axiosClient from "@/shared/lib/axios/axios.ts";
import { API_ROUTES } from "@/users/api/config";

const UserDetail = () => {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const { id } = useParams();

  const [user, setUser] = useState<any>(null);

  const { data: organizationsData, isLoading: orgsLoading } =
    useGetOrganizations();

  const organizations = organizationsData?.data || [];

  useEffect(() => {
    if (state?.user) {
      setUser({
        ...state.user,
        organizations: state.user.organizations || [],
      });
    }
  }, [state?.user, id]);

  const breadcrumbs = [
    { path: "/settings/user", breadcrumb: t("breadcrumbs.users") },
    { path: `/settings/user/${id}`, breadcrumb: user?.username || t("breadcrumbs.user") },
  ];

  if (!user) {
    return (
      <div className="h-full">
        <PageHeader breadcrumbs={breadcrumbs} />
        <div className="p-6">
          <p className="text-muted-foreground">
            {t("userDetail.unavailable")}
          </p>
        </div>
      </div>
    );
  }

  const handleChange = (field: string, value: string) => {
    setUser((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleOrganization = (orgId: number) => {
    setUser((prev: any) => {
      const exists = prev.organizations.includes(orgId);

      return {
        ...prev,
        organizations: exists
          ? prev.organizations.filter((id: number) => id !== orgId)
          : [...prev.organizations, orgId],
      };
    });
  };

  const handleCancel = () => {
    navigate("/settings/user");
  };

  const handleSave = async () => {
    const payload = {
      first_name: user.first_name,
      last_name: user.last_name,
      username: user.username,
      email: user.email,
      organizations: user.organizations,
      is_active: true,
    };

    await axiosClient.patch(`${API_ROUTES.USER}${id}/`, payload);

    navigate("/settings/user");
  };

  return (
    <div className="h-full">
      <PageHeader breadcrumbs={breadcrumbs} />

      <div className="p-6 space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl font-bold">{user.username}</h1>
          <p className="text-muted-foreground">
            {t("userDetail.description")}
          </p>
        </div>

        {/* User info */}
        <Card>
          <CardHeader>
            <CardTitle>{t("userDetail.infoTitle")}</CardTitle>
            <CardDescription>
              {t("userDetail.infoDescription")}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("userDetail.firstNameLabel")}</Label>
                <Input
                  value={user.first_name || ""}
                  onChange={(e) =>
                    handleChange("first_name", e.target.value)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>{t("userDetail.lastNameLabel")}</Label>
                <Input
                  value={user.last_name || ""}
                  onChange={(e) =>
                    handleChange("last_name", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("userDetail.usernameLabel")}</Label>
              <Input
                value={user.username}
                onChange={(e) =>
                  handleChange("username", e.target.value)
                }
              />
            </div>

            <div className="space-y-2">
              <Label>{t("userDetail.emailLabel")}</Label>
              <Input
                value={user.email || ""}
                onChange={(e) =>
                  handleChange("email", e.target.value)
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Organizations */}
        <Card>
          <CardHeader>
            <CardTitle>{t("userDetail.organizationsTitle")}</CardTitle>
            <CardDescription>
              {t("userDetail.organizationsDescription")}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-2">
            {orgsLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : organizations.length > 0 ? (
              organizations.map((org) => (
                <label
                  key={org.id}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={user.organizations.includes(org.id)}
                    onChange={() => toggleOrganization(org.id)}
                  />
                  <span>{org.name}</span>
                </label>
              ))
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("userDetail.noOrganizations")}
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={handleCancel}>
            {t("actions.cancel")}
          </Button>
          <Button onClick={handleSave}>
            {t("userDetail.save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
